// Workbench — preview-only "strategic signal workbench" routes.
//
// Layout: tab strip (Market · Flat / Market · Grouped / Client) with a
// shared toolbar above each table (search + filters + reset + visible
// row count). Filters and sort state are local to each variant so
// switching tabs preserves user intent.

import React, { useEffect, useMemo, useState } from "react";
import useMarketTracks from "./hooks/useMarketTracks";
import useClientView from "./hooks/useClientView";
import { useAuth } from "./auth/AuthContext";
import { dedupeTrackCards, sortTrackCards } from "./lib/clients";
import {
    externalRoleLabel, marketZoneLabel, evidenceLabel, thisWeekMovementLabel,
} from "./lib/workbench/labels";
import MarketWorkbenchTable from "./components/workbench/MarketWorkbenchTable";
import ClientWorkbenchTable from "./components/workbench/ClientWorkbenchTable";
import SelectedSignalBrief from "./components/workbench/SelectedSignalBrief";
import SelectedClientSignalBrief from "./components/workbench/SelectedClientSignalBrief";

const TABS = [
    { id: "market-flat", label: "Market · Flat" },
    { id: "market-grouped", label: "Market · Grouped" },
    { id: "client", label: "Client" },
];

const TabStrip = ({ active, onChange }) => (
    <div className="flex border border-[hsl(var(--line))] rounded-sm overflow-hidden" role="tablist" data-testid="workbench-tabs">
        {TABS.map((t) => (
            <button
                key={t.id}
                role="tab"
                aria-selected={active === t.id}
                data-testid={`workbench-tab-${t.id}`}
                onClick={() => onChange(t.id)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${active === t.id ? "bg-[hsl(var(--ink))] text-white" : "bg-[hsl(var(--surface))] text-[hsl(var(--ink-soft))] hover:bg-[hsl(var(--surface-sunken))]"}`}
            >
                {t.label}
            </button>
        ))}
    </div>
);

const PreviewBanner = () => (
    <div className="card-flat p-3 border-l-4 border-l-[hsl(var(--accent))]" data-testid="workbench-preview-banner">
        <div className="flex items-start gap-3">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-[hsl(var(--accent-ink))] mt-0.5">Preview</span>
            <div className="text-[12px] text-[hsl(var(--ink-soft))] leading-snug">
                Strategic signal workbench — preview only. Same data as the Market &amp; Client views; different framing. Does not replace the current views.
            </div>
        </div>
    </div>
);

const FilterSelect = ({ label, value, onChange, options, testid }) => (
    <label className="flex items-center gap-1.5 text-[12px]">
        <span className="text-[hsl(var(--mute))]">{label}</span>
        <select
            value={value || ""}
            onChange={(e) => onChange(e.target.value || null)}
            data-testid={testid}
            className="px-2 py-1 text-[12px] bg-[hsl(var(--surface-sunken))] border border-[hsl(var(--line))] rounded"
        >
            <option value="">all</option>
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
    </label>
);

const Toolbar = ({ search, onSearch, rowCount, totalCount, onReset, children }) => (
    <div className="card-flat p-3 flex items-center gap-3 flex-wrap" data-testid="workbench-toolbar">
        <input
            type="search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search signals…"
            data-testid="workbench-search"
            className="px-3 py-1.5 text-sm bg-[hsl(var(--surface-sunken))] border border-[hsl(var(--line))] rounded min-w-[220px]"
        />
        {children}
        <div className="ml-auto flex items-center gap-3">
            <span className="text-[11px] text-[hsl(var(--mute))] num" data-testid="workbench-row-count">
                Showing {rowCount} of {totalCount}
            </span>
            <button
                type="button"
                onClick={onReset}
                data-testid="workbench-reset"
                className="text-[11px] px-2 py-1 border border-[hsl(var(--line))] rounded hover:bg-[hsl(var(--surface-sunken))] text-[hsl(var(--ink-soft))]"
            >
                Reset
            </button>
        </div>
    </div>
);

// ─── MARKET FILTERS ─────────────────────────────────────────────────────────

const matchesText = (track, q) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    const hay = [
        track.StableTrackName, track.CanonicalTrackName, track.SuggestedDisplayLabel,
        track.LatestWeeklyFraming, track.MacroTheme, track?._governed?.marketZone,
        track.LifecycleStatus, track._role,
        track.TrackThesis,
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(needle);
};

const ROLE_OPTIONS = [
    { value: "CORE_TRACK", label: "Core signal" },
    { value: "WATCHLIST_TRACK", label: "Watchlist signal" },
    { value: "SUB_PATTERN", label: "Supporting pattern" },
    { value: "WEEKLY_DYNAMIC", label: "This week's movement" },
];

const MOMENTUM_FILTERS = [
    { value: "strengthen", label: "Strengthening" },
    { value: "soften", label: "Softening" },
    { value: "stable", label: "Stable" },
    { value: "broaden", label: "Broadening" },
    { value: "new", label: "New evidence" },
    { value: "no current", label: "No current movement" },
];

const EVIDENCE_OPTIONS = [
    { value: "broad", label: "Broad evidence base" },
    { value: "narrow", label: "Narrow evidence base" },
    { value: "single-week", label: "Single-week evidence" },
    { value: "limited", label: "Limited evidence" },
];

const useMarketFilters = (tracks, historyByTrackId) => {
    const [search, setSearch] = useState("");
    const [role, setRole] = useState(null);
    const [zone, setZone] = useState(null);
    const [momentum, setMomentum] = useState(null);
    const [evidence, setEvidence] = useState(null);

    const zoneOptions = useMemo(() => {
        const set = new Set();
        for (const t of tracks) {
            const z = marketZoneLabel(t);
            if (z && z !== "—") set.add(z);
        }
        return Array.from(set).sort().map((z) => ({ value: z, label: z }));
    }, [tracks]);

    const filtered = useMemo(() => {
        return tracks.filter((t) => {
            if (role && t._role !== role) return false;
            if (zone && marketZoneLabel(t) !== zone) return false;
            if (momentum) {
                const mv = (thisWeekMovementLabel(t) || "").toLowerCase();
                if (!mv.includes(momentum)) return false;
            }
            if (evidence) {
                const ev = (evidenceLabel(t) || "").toLowerCase();
                if (!ev.includes(evidence)) return false;
            }
            return matchesText(t, search);
        });
    }, [tracks, search, role, zone, momentum, evidence]);

    const reset = () => { setSearch(""); setRole(null); setZone(null); setMomentum(null); setEvidence(null); };
    return { filtered, search, setSearch, role, setRole, zone, setZone, momentum, setMomentum, evidence, setEvidence, zoneOptions, reset };
};

const MarketToolbar = ({ filters }) => (
    <>
        <FilterSelect label="Role" value={filters.role} onChange={filters.setRole} options={ROLE_OPTIONS} testid="filter-market-role" />
        <FilterSelect label="Zone" value={filters.zone} onChange={filters.setZone} options={filters.zoneOptions} testid="filter-market-zone" />
        <FilterSelect label="This week" value={filters.momentum} onChange={filters.setMomentum} options={MOMENTUM_FILTERS} testid="filter-market-momentum" />
        <FilterSelect label="Evidence" value={filters.evidence} onChange={filters.setEvidence} options={EVIDENCE_OPTIONS} testid="filter-market-evidence" />
    </>
);

// ─── VARIANTS ───────────────────────────────────────────────────────────────

const MarketFlatVariant = ({ tracks, historyByTrackId, selectedTrackId, setSelectedTrackId, selectedTrack, selectedTrackHistory }) => {
    const filters = useMarketFilters(tracks, historyByTrackId);
    const [sortBy, setSortBy] = useState("score");
    const [sortDir, setSortDir] = useState("desc");
    const onSort = (key, dir) => { setSortBy(key); setSortDir(dir); };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <h2 className="text-[18px] font-semibold text-[hsl(var(--ink))] leading-tight">Strategic signal workbench · flat</h2>
                    <p className="text-[12px] text-[hsl(var(--mute))] mt-0.5">Sortable workbench table · click any column header to reorder, click a row to inspect.</p>
                </div>
            </div>
            <Toolbar search={filters.search} onSearch={filters.setSearch} rowCount={filters.filtered.length} totalCount={tracks.length} onReset={filters.reset}>
                <MarketToolbar filters={filters} />
            </Toolbar>
            <MarketWorkbenchTable
                variant="flat"
                tracks={filters.filtered}
                historyByTrackId={historyByTrackId}
                selectedTrackId={selectedTrackId}
                onSelectTrack={setSelectedTrackId}
                sortBy={sortBy} sortDir={sortDir} onSort={onSort}
            />
            <SelectedSignalBrief
                track={selectedTrack}
                history={selectedTrackHistory}
                allTracks={tracks}
                onSelectTrack={setSelectedTrackId}
            />
        </div>
    );
};

const MarketGroupedVariant = ({ tracks, historyByTrackId, selectedTrackId, setSelectedTrackId, selectedTrack, selectedTrackHistory }) => {
    const [groupBy, setGroupBy] = useState("parent");
    const filters = useMarketFilters(tracks, historyByTrackId);
    const [sortBy, setSortBy] = useState("score");
    const [sortDir, setSortDir] = useState("desc");
    const onSort = (key, dir) => { setSortBy(key); setSortDir(dir); };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-[18px] font-semibold text-[hsl(var(--ink))] leading-tight">Strategic signal workbench · grouped</h2>
                    <p className="text-[12px] text-[hsl(var(--mute))] mt-0.5">
                        {groupBy === "parent"
                            ? "Main signal stays first in its group · supporting patterns and weekly movements sort beneath."
                            : "Signals grouped by market zone · click any header to reorder."}
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[12px]" data-testid="workbench-group-by">
                    <span className="text-[hsl(var(--mute))]">Group by</span>
                    <div className="flex border border-[hsl(var(--line))] rounded-sm overflow-hidden">
                        <button type="button" onClick={() => setGroupBy("parent")} data-testid="group-by-parent"
                            className={`px-3 py-1 text-[11px] font-medium ${groupBy === "parent" ? "bg-[hsl(var(--ink))] text-white" : "bg-[hsl(var(--surface))] text-[hsl(var(--ink-soft))] hover:bg-[hsl(var(--surface-sunken))]"}`}>
                            Parent signal
                        </button>
                        <button type="button" onClick={() => setGroupBy("zone")} data-testid="group-by-zone"
                            className={`px-3 py-1 text-[11px] font-medium ${groupBy === "zone" ? "bg-[hsl(var(--ink))] text-white" : "bg-[hsl(var(--surface))] text-[hsl(var(--ink-soft))] hover:bg-[hsl(var(--surface-sunken))]"}`}>
                            Market zone
                        </button>
                    </div>
                </div>
            </div>
            <Toolbar search={filters.search} onSearch={filters.setSearch} rowCount={filters.filtered.length} totalCount={tracks.length} onReset={filters.reset}>
                <MarketToolbar filters={filters} />
            </Toolbar>
            <MarketWorkbenchTable
                variant="grouped"
                tracks={filters.filtered}
                historyByTrackId={historyByTrackId}
                selectedTrackId={selectedTrackId}
                onSelectTrack={setSelectedTrackId}
                groupBy={groupBy}
                sortBy={sortBy} sortDir={sortDir} onSort={onSort}
            />
            <SelectedSignalBrief
                track={selectedTrack}
                history={selectedTrackHistory}
                allTracks={tracks}
                onSelectTrack={setSelectedTrackId}
            />
        </div>
    );
};

// ─── CLIENT VARIANT ─────────────────────────────────────────────────────────

const matchesClientText = (card, q) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    const hay = [
        card.stableTrackName, card.canonicalTrackId, card.trackId,
        card?.market?.marketZone, card?.marketZone, card?.macroTheme,
        card?.client?.positioning, card?.client?.relevanceExplanation,
        card?.client?.relevanceFrontendSummary,
        card?.client?.positioningRecommendedNextAction,
        card?.client?.relevanceRationale, card?.client?.selectionRationale,
        card?.clientRelevanceRationale,
        card?.client?.cardCategory,
    ].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(needle);
};

const CLIENT_CATEGORY_OPTIONS = [
    { value: "OPPORTUNITY", label: "Opportunity" },
    { value: "THREAT", label: "Threat" },
    { value: "VISIBILITY_GAP", label: "Visibility gap" },
    { value: "CAPABILITY_GAP", label: "Capability gap" },
    { value: "MONITOR", label: "Monitor" },
    { value: "EXPLORE", label: "Explore" },
    { value: "ACT", label: "Act" },
];
const CLIENT_LEVEL_OPTIONS = [
    { value: "HIGH", label: "High" }, { value: "MEDIUM", label: "Medium" }, { value: "LOW", label: "Low" },
];

const ClientVariant = () => {
    const [clientId, setClientId] = useState(null);
    const cv = useClientView({ clientId });
    useEffect(() => {
        if (!clientId && cv.defaultClientId) setClientId(cv.defaultClientId);
    }, [clientId, cv.defaultClientId]);
    const [selectedKey, setSelectedKey] = useState(null);

    const trackCards = useMemo(() => {
        const cards = cv.trackCards || [];
        const ctx = { clientId: clientId || "", selectedWeek: cv.selectedYearWeek || cv.latestYearWeek || "" };
        return sortTrackCards(dedupeTrackCards(cards, ctx));
    }, [cv.trackCards, clientId, cv.selectedYearWeek, cv.latestYearWeek]);

    // Filters / search / sort
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState(null);
    const [exposure, setExposure] = useState(null);
    const [urgency, setUrgency] = useState(null);
    const [momentum, setMomentum] = useState(null);
    const [zone, setZone] = useState(null);
    const [sortBy, setSortBy] = useState("relevanceScore");
    const [sortDir, setSortDir] = useState("desc");

    const zoneOptions = useMemo(() => {
        const set = new Set();
        for (const c of trackCards) {
            const z = c?.market?.marketZone || c?.marketZone || c?.macroTheme;
            if (z) set.add(z);
        }
        return Array.from(set).sort().map((z) => ({ value: z, label: z }));
    }, [trackCards]);

    const filtered = useMemo(() => trackCards.filter((c) => {
        if (category && (c?.client?.cardCategory || "").toUpperCase() !== category) return false;
        if (exposure && (c?.client?.relevanceLevel || "").toUpperCase() !== exposure) return false;
        if (urgency && (c?.client?.urgency || "").toUpperCase() !== urgency) return false;
        if (zone) {
            const z = c?.market?.marketZone || c?.marketZone || c?.macroTheme;
            if (z !== zone) return false;
        }
        if (momentum) {
            const mv = (c?.market?.momentumLabel || c?.market?.momentum || "").toLowerCase();
            if (!mv.includes(momentum)) return false;
        }
        return matchesClientText(c, search);
    }, [trackCards, search, category, exposure, urgency, momentum, zone]), [trackCards, search, category, exposure, urgency, momentum, zone]);

    const reset = () => { setSearch(""); setCategory(null); setExposure(null); setUrgency(null); setMomentum(null); setZone(null); };

    const validTrackIds = useMemo(() => new Set(trackCards.map((c) => c.canonicalTrackId || c.trackId).filter(Boolean)), [trackCards]);
    const trackLabelById = useMemo(() => {
        const m = new Map();
        for (const c of trackCards) {
            const id = c.canonicalTrackId || c.trackId;
            if (id) m.set(id, c.stableTrackName || id);
        }
        return m;
    }, [trackCards]);

    const selectedCard = useMemo(() => {
        if (!selectedKey) return null;
        return trackCards.find((c) => (c.canonicalTrackId || c.trackId || c.stableTrackName) === selectedKey) || null;
    }, [selectedKey, trackCards]);

    const handleSelect = (card) => setSelectedKey(card.canonicalTrackId || card.trackId || card.stableTrackName);
    const onSort = (key, dir) => { setSortBy(key); setSortDir(dir); };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-[18px] font-semibold text-[hsl(var(--ink))] leading-tight">Client signal workbench</h2>
                    <p className="text-[12px] text-[hsl(var(--mute))] mt-0.5">Click any column header to reorder · click a row to inspect · organizational exposure shown beneath the brief.</p>
                </div>
                {cv.clients?.length > 0 && (
                    <select
                        data-testid="workbench-client-select"
                        value={clientId || ""}
                        onChange={(e) => { setClientId(e.target.value); setSelectedKey(null); }}
                        className="px-2 py-1.5 text-sm bg-[hsl(var(--surface-sunken))] border border-[hsl(var(--line))] rounded"
                    >
                        {cv.clients.map((c) => (
                            <option key={c.id} value={c.id}>{c.name || c.id}</option>
                        ))}
                    </select>
                )}
            </div>
            {cv.loading && <div className="card-flat p-6 text-sm text-[hsl(var(--mute))]">Loading client signal cards…</div>}
            {cv.error && <div className="card-flat p-6 text-sm text-[hsl(var(--negative))]">{cv.error?.message || "Failed to load client view"}</div>}
            {!cv.loading && !cv.error && (
                <>
                    <Toolbar search={search} onSearch={setSearch} rowCount={filtered.length} totalCount={trackCards.length} onReset={reset}>
                        <FilterSelect label="Lens" value={category} onChange={setCategory} options={CLIENT_CATEGORY_OPTIONS} testid="filter-client-category" />
                        <FilterSelect label="Exposure" value={exposure} onChange={setExposure} options={CLIENT_LEVEL_OPTIONS} testid="filter-client-exposure" />
                        <FilterSelect label="Urgency" value={urgency} onChange={setUrgency} options={CLIENT_LEVEL_OPTIONS} testid="filter-client-urgency" />
                        <FilterSelect label="Momentum" value={momentum} onChange={setMomentum} options={MOMENTUM_FILTERS} testid="filter-client-momentum" />
                        <FilterSelect label="Zone" value={zone} onChange={setZone} options={zoneOptions} testid="filter-client-zone" />
                    </Toolbar>
                    <ClientWorkbenchTable
                        trackCards={filtered}
                        selectedCardKey={selectedKey}
                        onSelect={handleSelect}
                        sortBy={sortBy} sortDir={sortDir} onSort={onSort}
                    />
                    <SelectedClientSignalBrief
                        card={selectedCard}
                        validTrackIds={validTrackIds}
                        trackLabelById={trackLabelById}
                        exposureMatrix={cv.organizationalExposureMatrix}
                        impactCellAssessments={cv.impactCellAssessments}
                        clientProfile={cv.clientProfile}
                    />
                </>
            )}
        </div>
    );
};

const Workbench = () => {
    const { isAdmin } = useAuth();
    const [active, setActive] = useState("market-flat");

    const [selectedTrackId, setSelectedTrackId] = useState(null);
    const market = useMarketTracks({ selectedTrackId, showExploratory: true });
    const selectedTrack = useMemo(
        () => (market.tracks || []).find((t) => t.TrackID === selectedTrackId) || null,
        [market.tracks, selectedTrackId]
    );
    const selectedTrackHistory = useMemo(
        () => (selectedTrackId && market.historyByTrackId) ? (market.historyByTrackId[selectedTrackId] || []) : [],
        [market.historyByTrackId, selectedTrackId]
    );

    if (!isAdmin) {
        return (
            <div className="card-flat p-8 text-center" data-testid="workbench-no-access">
                <div className="text-sm text-[hsl(var(--ink))] font-medium">Admin access required</div>
                <div className="text-[12px] text-[hsl(var(--mute))] mt-1">The Workbench preview is restricted to admin users.</div>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="workbench-page">
            <PreviewBanner />
            <TabStrip active={active} onChange={setActive} />
            {market.loading && active !== "client" && <div className="card-flat p-6 text-sm text-[hsl(var(--mute))]">Loading signals…</div>}
            {market.error && active !== "client" && <div className="card-flat p-6 text-sm text-[hsl(var(--negative))]">{String(market.error)}</div>}
            {!market.loading && !market.error && active === "market-flat" && (
                <MarketFlatVariant
                    tracks={market.tracks || []}
                    historyByTrackId={market.historyByTrackId}
                    selectedTrackId={selectedTrackId}
                    setSelectedTrackId={setSelectedTrackId}
                    selectedTrack={selectedTrack}
                    selectedTrackHistory={selectedTrackHistory}
                />
            )}
            {!market.loading && !market.error && active === "market-grouped" && (
                <MarketGroupedVariant
                    tracks={market.tracks || []}
                    historyByTrackId={market.historyByTrackId}
                    selectedTrackId={selectedTrackId}
                    setSelectedTrackId={setSelectedTrackId}
                    selectedTrack={selectedTrack}
                    selectedTrackHistory={selectedTrackHistory}
                />
            )}
            {active === "client" && <ClientVariant />}
        </div>
    );
};

export default Workbench;
